import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import { pageTypeConstants } from '../../constants/pageTypeConstants';
import * as tracking from 'dibs-tracking';

import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespRefineMenuSelector } from '../SbRespRefineMenuSelector/SbRespRefineMenuSelector';

import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader';
import { SbRespAppliedFilters } from '../SbRespAppliedFilters/SbRespAppliedFilters';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems';
import { SbSharedRecentlySoldItems } from '../SbSharedRecentlySoldItems/SbSharedRecentlySoldItems';
import { SharedPagination } from '../../shared/SharedPagination/SharedPagination';
import { SbRespRefineTopNav } from '../SbRespRefineTopNav/SbRespRefineTopNav';
import { SharedBottomModule } from '../../shared/SharedBottomModule/SharedBottomModule';

import { VERTICALS } from '../../constants/uriConstants';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';

import styles from './SbRespMainContent.scss';

function SbRespMainContentComponent({
    currency,
    itemSearch,
    viewer,
    user,
    isClient,
    isThinPage,
    isThinBrowsePage,
    relay,
    generatedUriRef,
    showMeasurements,
    page,
}) {
    const {
        topCategoryL1,
        displayUriRef,
        displayMaxNumberOfPages,
        pageType,
        bottomModule,
    } = itemSearch;

    const { canShowInternalSellerName, canShowSellerName, canShowStorefrontOnlyBadge } = user || {};

    const isFurniture = topCategoryL1 === VERTICALS.FURNITURE;
    const canShowCreatorName = !canShowSellerName && isFurniture;
    const isExclusivePage = pageType === pageTypes.EXCLUSIVE;

    const sharedProps = {
        currency,
        itemSearch,
        viewer,
        user,
        isClient,
    };

    const { isSbTopNavVariant, isSbUsabilityUpdatesVariant } = useContext(ServerVarsContext);
    useEffect(() => {
        const trackingData = getAbTestTrackingData(document.cookie, 'SB_TOP_FILTER_TEST');
        tracking.trackAbTestEvent(trackingData, pageTypeConstants.BROWSE);
        const sbUsabilityTrackingData = getAbTestTrackingData(
            document.cookie,
            'SB_USABILITY_UPDATES'
        );
        tracking.trackAbTestEvent(sbUsabilityTrackingData, 'sb_pageview');
    }, []);

    return (
        <div
            className={classnames(styles.mainContentContainer, {
                isSbTopNavVariant,
            })}
        >
            {!isSbTopNavVariant && (
                <SbRespRefineMenuSelector
                    {...sharedProps}
                    uriRef={generatedUriRef}
                    isThinPage={isThinPage}
                />
            )}
            <div className={styles.resultsContainer}>
                {isSbTopNavVariant ? (
                    <SbRespRefineTopNav
                        itemSearch={itemSearch}
                        viewer={viewer}
                        user={user}
                        currency={currency}
                        isClient={isClient}
                    />
                ) : (
                    <div className={styles.resultsHeader}>
                        <div className={styles.sortContainer}>
                            <SbRespSortItems itemSearch={itemSearch} />
                            <SbRespPageSize itemSearch={itemSearch} />
                        </div>
                        {!isSbUsabilityUpdatesVariant && (
                            <SbRespBrowseHeader itemSearch={itemSearch} />
                        )}
                    </div>
                )}
                {isSbUsabilityUpdatesVariant && (
                    <SbRespAppliedFilters
                        user={user}
                        viewer={viewer}
                        currency={currency}
                        itemSearch={itemSearch}
                    />
                )}
                <div className={styles.resultsContainer}>
                    {/* Item Results */}
                    <SbRespSearchResultContainer
                        {...sharedProps}
                        showInternalSellerName={canShowInternalSellerName}
                        showStorefrontOnlyBadge={
                            canShowStorefrontOnlyBadge && pageType === pageTypes.DEALER
                        }
                        showSellerName={!!canShowSellerName}
                        showCreatorName={canShowCreatorName}
                        showMeasurements={showMeasurements}
                        relay={relay}
                    />
                    {/* Related Items */}
                    {isThinBrowsePage && !isExclusivePage && (
                        <>
                            <SbSharedRelatedItems
                                itemSearch={itemSearch}
                                user={user}
                                currency={currency}
                                isClient={isClient}
                                viewer={viewer}
                            />
                            <SbSharedRecentlySoldItems
                                itemSearch={itemSearch}
                                user={user}
                                currency={currency}
                                isClient={isClient}
                                viewer={viewer}
                            />
                        </>
                    )}

                    {/* Pagination */}
                    <div className={styles.paginationContainer}>
                        <SharedPagination
                            currentPage={page}
                            numberOfPages={displayMaxNumberOfPages}
                            displayUriRef={displayUriRef}
                        />
                    </div>

                    <SharedBottomModule
                        className={styles.bottomModule}
                        bottomModule={bottomModule}
                    />
                </div>
            </div>
        </div>
    );
}

SbRespMainContentComponent.propTypes = {
    currency: PropTypes.string.isRequired,
    itemSearch: PropTypes.shape({
        displayUriRef: PropTypes.string.isRequired,
        topCategoryL1: PropTypes.string,
        displayMaxNumberOfPages: PropTypes.number,
        pageType: PropTypes.string,
        bottomModule: PropTypes.array,
        appliedFilters: PropTypes.array,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    isThinPage: PropTypes.bool.isRequired,
    isThinBrowsePage: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    generatedUriRef: PropTypes.string,
    showMeasurements: PropTypes.bool,
    page: PropTypes.number,
};

const mapStateToProps = ({ displayPreferences, filters }) => ({
    showMeasurements: displayPreferences.showMeasurements,
    generatedUriRef: filters.generatedUriRef,
    page: filters.page,
});

export const SbRespMainContent = createFragmentContainer(
    connect(mapStateToProps)(SbRespMainContentComponent),
    {
        itemSearch: graphql`
            fragment SbRespMainContent_itemSearch on ItemSearchQueryConnection {
                topCategoryL1
                displayUriRef
                pageType
                displayMaxNumberOfPages
                bottomModule {
                    ...SharedBottomModule_bottomModule
                }
                ...SbRespRefineMenuSelector_itemSearch
                ...SbRespSortItems_itemSearch
                ...SbRespPageSize_itemSearch
                ...SbRespBrowseHeader_itemSearch
                ...SbRespSearchResultContainer_itemSearch
                ...SbSharedRelatedItems_itemSearch
                ...SbRespRefineTopNav_itemSearch
                ...SbSharedRecentlySoldItems_itemSearch
                ...SbRespAppliedFilters_itemSearch
            }
        `,
        user: graphql`
            fragment SbRespMainContent_user on User {
                canShowSellerName
                canShowInternalSellerName: hasPermission(name: "VIEW_SELLER_NAME")
                canShowStorefrontOnlyBadge: hasPermission(name: "VIEW_STOREFRONT_ONLY_BADGE")
                ...SbRespRefineMenuSelector_user
                ...SbRespSearchResultContainer_user
                ...SbSharedRelatedItems_user
                ...SbRespRefineTopNav_user
                ...SbSharedRecentlySoldItems_user
                ...SbRespAppliedFilters_user
            }
        `,
        viewer: graphql`
            fragment SbRespMainContent_viewer on Viewer {
                ...SbRespRefineMenuSelector_viewer
                ...SbRespSearchResultContainer_viewer
                ...SbSharedRelatedItems_viewer
                ...SbRespRefineTopNav_viewer
                ...SbSharedRecentlySoldItems_viewer
                ...SbRespAppliedFilters_viewer
            }
        `,
    }
);
